import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import moment from 'moment';

import axios from '../../../axios-url';
import * as actions from '../../../store/actions/index';

import { F, raw } from './RestaurantOrderFields';
import { buildStageFields, buildMenuItemRow } from './RestaurantOrderState';
import {
    updateContainerFieldValue, addContainerRow, removeContainerRow,
} from './containerHelpers';
import { ORDER_TYPES } from '../../Layout/POSSidebar';
import SpecialOrderMenu from '../Info/SpecialOrderMenu';
import SpecialOrderFooter from '../Info/SpecialOrderFooter';
import EmployeeLoginLink from '../Info/EmployeeLoginLink';
import { openRazorpayCheckout } from './razorpayCheckout';
import razorpayAxios from './razorpay-axios';
import ChenthoorLogo from '../../../assets/images/Chenthoor_landscape_Logo-removebg-preview.png';
import ChenthoorIcon from '../../../assets/images/chendhur_logo_icon_nobg.png';

const RESTAURANT_ORDER_ID = 'f7405b60-9933-4b08-a8be-107aa21a05f7';
const PROCESS_NUM = '303';
const STAGE_NUM = '001';

// The category the page opens on when the link does not name one. This page is
// where "/" lands (GUEST_HOME_PATH), and a guest arrives there with no query
// string at all, so the offers the page exists to sell have to be what it opens
// on. Matched against the MenuCategory master the same way a ?category= value
// is — by label or id, case and spacing insensitive — so "SPECIALOFFER" also
// finds a category named "Special Offer". A ?category= in the link still wins.
const DEFAULT_CATEGORY = 'SPECIALOFFER';

// Where a special order's KOT rows start: the customer has confirmed, but no
// one in the kitchen has accepted it yet. A real RESSTATUS row (KOT/15,
// sequence 0) sitting one step before KOT/CONFIRMED (statusId 10, sequence
// 1), so it's resolved out of the fetched master by name like every other
// status here — never hardcoded.
const KOT_CUSTOMER_CONFIRMED = 'CONFIRMED BY CUSTOMER';

// The PAYMENT row an online-payment order is saved with (PAYMENT/51 in the
// live master), resolved by name like the statuses above.
const PAYMENT_PENDING = 'PENDING';
const ONLINE_PAYMENT_TYPE = 'Online';

// TODO: still no RESSTATUS row for "scheduled, not yet dispatched" in the
// backend's master (checked against GetresActionStatusflow on 2026-09-09 —
// DELIVERY only has OUT FOR DELIVERY / DELIVERED / RETURNED / FAILED /
// CANCLLED), so this one stays a local placeholder until it exists.
const DELIVERY_SCHEDULED_STATUS = {
    Id: 0, ActionId: 3, ActionName: 'DELIVERY', StatusId: 0, StatusName: 'SCHEDULED', Sequence: 0,
};

const MENU_IMAGE_BASE_URL = `${axios.defaults.baseURL}/MenuItemsimage/`;

const TIME_PICKER_HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const TIME_PICKER_MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

const KEYPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace'];

// Tapping the quantity number on an already-in-cart item (not the +/-
// buttons themselves) opens this instead of turning that number into a
// plain inline-editable field — a deliberate keypad, not free typing.
const QtyKeypadModal = ({ itemName, value, onDigit, onBackspace, onClear, onConfirm, onCancel }) => {
    const qty = parseInt(value, 10);
    const canConfirm = Number.isInteger(qty) && qty >= 0;

    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key >= '0' && e.key <= '9') {
                e.preventDefault();
                onDigit(e.key);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (canConfirm) onConfirm();
            } else if (e.key === 'Backspace') {
                e.preventDefault();
                onBackspace();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [canConfirm, onDigit, onConfirm, onBackspace, onCancel]);

    return (
        <div className="so-keypad-backdrop" onClick={onCancel}>
            <div className="so-keypad-modal" onClick={(e) => e.stopPropagation()}>
                <div className="so-keypad-modal__header">
                    <div>
                        <span className="so-keypad-modal__title">{itemName}</span>
                        <span className="so-keypad-modal__subtitle">Enter quantity</span>
                    </div>
                    <button type="button" className="so-keypad-modal__close" onClick={onCancel} aria-label="Close">
                        <i className="pi pi-times" />
                    </button>
                </div>

                <div className="so-keypad-modal__display">{value || '0'}</div>

                <div className="so-keypad-modal__grid">
                    {KEYPAD_KEYS.map((key) => {
                        if (key === 'clear') {
                            return (
                                <button type="button" key={key} className="so-keypad-modal__key so-keypad-modal__key--action" onClick={onClear}>
                                    C
                                </button>
                            );
                        }
                        if (key === 'backspace') {
                            return (
                                <button type="button" key={key} className="so-keypad-modal__key so-keypad-modal__key--action" onClick={onBackspace} aria-label="Backspace">
                                    <i className="pi pi-arrow-left" />
                                </button>
                            );
                        }
                        return (
                            <button type="button" key={key} className="so-keypad-modal__key" onClick={() => onDigit(key)}>
                                {key}
                            </button>
                        );
                    })}
                </div>

                <div className="so-keypad-modal__footer">
                    <button type="button" className="so-keypad-modal__btn so-keypad-modal__btn--cancel" onClick={onCancel}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="so-keypad-modal__btn so-keypad-modal__btn--confirm"
                        onClick={onConfirm}
                        disabled={!canConfirm}
                    >
                        {canConfirm ? (qty === 0 ? 'Remove' : `Set ${qty}`) : 'Set'}
                    </button>
                </div>
            </div>
        </div>
    );
};

class SpecialOrderDetails extends Component {
    state = {
        stageFields: null,
        // Set by the delivery-date picker's onChange; the save payload's
        // resdeliverydate reads from here.
        resdeliverydate: null,
        authReady: false,
        isSaving: false,
        orderPlaced: false,
        orderNumber: null,
        // 'browse' (offers list) → 'cart' (review selected items) →
        // 'checkout' (your details + line items + payment options).
        checkoutStep: 'browse',
        paymentMethod: 'cod',
        // Online payment, which runs after the order is saved:
        // null (not started) | 'processing' | 'paid' | 'failed' | 'cancelled'.
        onlinePaymentStatus: null,
        savedRequestId: null,
        savedTotal: 0,
        searchQuery: '',
        // Which category the offer list is showing. Seeded once from the
        // link's ?category= (see resolveInitialCategory), then owned by the
        // customer — they're free to browse every other category from the
        // tab strip afterwards.
        selectedCategoryId: null,
        categoryResolved: false,
        // RESSTATUS rows from the backend (see fetchResStatusMaster).
        // null = still loading, [] = loaded but unusable/failed.
        resStatusMaster: null,
        // Keypad for editing an already-in-cart quantity — opened by
        // tapping the number itself (not the +/- buttons) on either the
        // offer card stepper or the cart-review row stepper.
        keypadOpen: false,
        keypadRowIndex: null,
        keypadItemName: '',
        keypadValue: '',
    };

    // The currently-selected category tab, so the linked one can be scrolled
    // into view when it isn't the first in the strip.
    activeTabRef = React.createRef();

    openQtyKeypad = (rowIndex, itemName, currentQty) => {
        this.setState({
            keypadOpen: true,
            keypadRowIndex: rowIndex,
            keypadItemName: itemName,
            keypadValue: String(currentQty),
        });
    };

    closeQtyKeypad = () => {
        this.setState({ keypadOpen: false, keypadRowIndex: null, keypadItemName: '', keypadValue: '' });
    };

    pressKeypadDigit = (digit) => {
        this.setState((prev) => {
            if (prev.keypadValue.length >= 3) return null; // cap at 999
            return { keypadValue: prev.keypadValue + digit };
        });
    };

    pressKeypadBackspace = () => {
        this.setState((prev) => ({ keypadValue: prev.keypadValue.slice(0, -1) }));
    };

    pressKeypadClear = () => {
        this.setState({ keypadValue: '' });
    };

    confirmQtyKeypad = () => {
        const { keypadRowIndex, keypadValue } = this.state;
        const qty = parseInt(keypadValue, 10);
        if (!Number.isInteger(qty) || qty < 0) return;

        if (qty === 0) {
            this.removeFromCart(keypadRowIndex);
        } else {
            this.setCartQty(keypadRowIndex, qty);
        }
        this.closeQtyKeypad();
    };

    goToCart = () => this.setState({ checkoutStep: 'cart' });
    goToCheckout = () => this.setState({ checkoutStep: 'checkout' });
    backToBrowse = () => this.setState({ checkoutStep: 'browse' });
    backToCart = () => this.setState({ checkoutStep: 'cart' });

    componentDidMount() {
        // Guest access: reuse the real login flow with fixed credentials
        // (per the plan) instead of hand-bootstrapping an identity — same
        // `auth()` call CustomerSignIn.js makes on submit. Guarded like
        // QrGuestGate.js so a returning guest session (or staff who landed
        // here directly) isn't clobbered.
        if (!localStorage.getItem('EmpId')) {
            this.props.onAuth('9999', null , false, false, false);
        } else {
            this.afterAuthReady();
        }
    }

    componentDidUpdate(prevProps) {
        // `state.auth.token` defaults to a non-null placeholder (see
        // POSApp.js's own comment on this), so it can't signal "login done"
        // — `userObject.empID` only gets (re)populated once authSuccess
        // actually fires, which is the real completion signal here.
        const wasReady = Boolean(prevProps.loggedUserDetail && prevProps.loggedUserDetail.empID);
        const isReady = Boolean(this.props.loggedUserDetail && this.props.loggedUserDetail.empID);
        if (!wasReady && isReady) {
            this.afterAuthReady();
        }

        // Guarded internally and only ever fires once, so it's safe to run on
        // every update — the masters it needs land at their own pace.
        this.resolveInitialCategory();
    }

    afterAuthReady = () => {
        if (this.state.authReady) return;

        const freshFields = buildStageFields();
        freshFields[F.ORDER_TYPE] = {
            ...freshFields[F.ORDER_TYPE],
            FieldValue: ORDER_TYPES.DELIVERY,
        };

        this.setState({ authReady: true, stageFields: freshFields }, () => {
            this.props.getMasterKeywordList('MenuCategory');
            this.props.getMasterKeywordList('Modifier');
            this.props.getMasterData(
                'res/GetinvMenuItemList?companyId=' + this.props.loggedUserDetail.CompanyId
            );
            this.fetchResStatusMaster();
        });
    };

    // ── Generic field writers ───────────────────────────────────────────────
    setContainerField = (containerKey, childKey, value) => {
        this.setState(prev => ({
            stageFields: updateContainerFieldValue(prev.stageFields, containerKey, 0, childKey, value),
        }));
    };

    setFlatField = (key, value) => {
        this.setState(prev => ({
            stageFields: {
                ...prev.stageFields,
                [key]: { ...prev.stageFields[key], FieldValue: value },
            },
        }));
    };

    // Custom hour/minute/AM-PM picker — F.EXPECTED_DELIVERY_TIME is stored
    // as a plain 24h "HH:mm" string (an INPUT-type field, not a real time
    // type in this schema — see RestaurantOrderState.js); the 12h/AM-PM
    // split below is purely a display-layer convenience, reconstructed back
    // into that same "HH:mm" string on every change.
    getTimeParts = (value) => {
        if (!value) return { hour12: '', minute: '', period: 'AM' };
        const [h, m] = value.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return { hour12: '', minute: '', period: 'AM' };
        const period = h >= 12 ? 'PM' : 'AM';
        let hour12 = h % 12;
        if (hour12 === 0) hour12 = 12;
        return { hour12: String(hour12), minute: String(m).padStart(2, '0'), period };
    };

    setTimePart = (part, value) => {
        const current = this.getTimeParts(this.state.stageFields[F.EXPECTED_DELIVERY_TIME].FieldValue);
        const next = { ...current, [part]: value };

        let hour24 = parseInt(next.hour12 || '12', 10) % 12;
        if (next.period === 'PM') hour24 += 12;
        const minute = next.minute === '' ? '00' : next.minute;

        this.setFlatField(
            F.EXPECTED_DELIVERY_TIME,
            `${String(hour24).padStart(2, '0')}:${minute}`
        );
    };

    getTomorrow = () => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 1);
        return d;
    };

    // ── Offer category resolution ───────────────────────────────────────────
    // No "Offer" flag exists anywhere in the data model — a category is just
    // a MenuCategory master entry, and the link's ?category= (e.g.
    // ?category=SPECIALOFFER) only decides which one the page *opens* on:
    // matched against that master's label or id, normalized to lowercase
    // with spaces/dashes stripped so "SPECIALOFFER" finds "Special Offer".
    // Every other stocked category stays reachable from the tab strip, so
    // this is a starting point, not a filter.
    normalizeKey = (s) => (s || '').toString().toLowerCase().replace(/[\s-]+/g, '');

    getUrlCategoryParam = () => {
        const params = new URLSearchParams(this.props.location.search);
        return params.get('category') || DEFAULT_CATEGORY;
    };

    // Category ids get compared across two endpoints (the MenuCategory
    // keyword list vs. GetinvMenuItemList's menuCategoryId), and .NET is
    // free to hand back the same GUID in different cases, so never compare
    // them raw.
    categoryKey = (id) => (id || '').toString().toLowerCase();

    // Every category the MenuCategory keyword list knows about, shown as-is.
    // Deliberately NOT filtered down to categories that currently have items
    // tagged to them: most of the menu item master has a null menuCategoryId,
    // so that filter would silently hide real categories (and did — it left
    // only RICE and Soup standing) whenever the tagging falls behind.
    getBrowsableCategories = () => this.props.MenuCategory || [];

    // The MenuCategory keyword list arrives asynchronously, so this retries
    // on every update until it can actually resolve, then latches on
    // `categoryResolved` — once the customer starts switching categories
    // themselves, a late redux update must not yank them back to the link's
    // category.
    resolveInitialCategory = () => {
        if (this.state.categoryResolved) return;

        const categories = this.getBrowsableCategories();
        if (categories.length === 0) return;

        // Never empty: with no ?category= in the link this is DEFAULT_CATEGORY.
        const target = this.normalizeKey(this.getUrlCategoryParam());
        const matched = categories.find(
            (c) => this.normalizeKey(c.label) === target || this.normalizeKey(c.value) === target
        );

        // The master may not have the category asked for — a stale link, or
        // SPECIALOFFER renamed in the keyword master. Open on the first
        // category then, rather than on a blank list: every one of them is
        // browsable from the tab strip anyway. (Reading .value off the match
        // unguarded threw here whenever nothing matched, which included every
        // visit with no ?category= at all.)
        const opening = matched || categories[0];

        this.setState(
            { categoryResolved: true, selectedCategoryId: opening.value },
            this.scrollActiveTabIntoView
        );
    };

    selectCategory = (categoryId) => {
        if (categoryId === this.state.selectedCategoryId) return;
        // Drop the search term too — carrying "biryani" into the Desserts tab
        // just shows an empty list and reads as a broken category.
        this.setState({ selectedCategoryId: categoryId, searchQuery: '' });
    };

    // The linked category can sit anywhere in the strip, so bring it into
    // view on load. `block: 'nearest'` keeps this to the strip's own
    // horizontal overflow instead of scrolling the page past the hero.
    scrollActiveTabIntoView = () => {
        const el = this.activeTabRef.current;
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'center' });
    };

    getOfferItems = () => {
        const { selectedCategoryId } = this.state;
        if (!selectedCategoryId) return [];
        const selected = this.categoryKey(selectedCategoryId);
        return (this.props.menuitems || []).filter(
            (item) => this.categoryKey(item.menuCategoryId) === selected
        );
    };

    // Display label for the category currently being browsed (hero heading),
    // falling back to the link's raw ?category= text while the masters load.
    getCategoryLabel = () => {
        const matched = (this.props.MenuCategory || []).find(
            (c) => c.value === this.state.selectedCategoryId
        );
        return (matched && matched.label) || this.getUrlCategoryParam() || 'Current Offers';
    };

    getMenuImageUrl = (photopath) => (photopath ? `${MENU_IMAGE_BASE_URL}${photopath}` : null);

    // Whether a given offer item is already in the cart — used to switch
    // that card between an "ADD" button and a live qty stepper. Since this
    // page never collects modifiers, a menu item id can only ever have one
    // corresponding cart row (see addToCart's own dedupe match).
    findCartRowForItem = (itemId) => {
        const rows = this.state.stageFields[F.MENU_ITEMS].ContainerFields;
        const rowIndex = rows.findIndex((row) => row[F.MI_ITEM]?.FieldValue === itemId);
        if (rowIndex === -1) return null;
        return { rowIndex, qty: Number(rows[rowIndex][F.MI_QTY]?.FieldValue || 0) };
    };

    // ── Cart ─────────────────────────────────────────────────────────────────
    // Same MI_MODIFIER_SEL array-not-string handling QrOrderDetails.js/
    // RestaurantOrder.js rely on (type-3 MULTIDROPDOWN field).
    parseModifierIds = (value) => {
        if (Array.isArray(value)) return value;
        if (!value) return [];
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) return parsed;
            } catch (e) { /* not JSON — treat as a plain comma list */ }
            return value.split(',').map((id) => id.trim()).filter(Boolean);
        }
        return [];
    };

    addToCart = (item) => {
        const modifierIds = Array.isArray(item.modifierIds) ? item.modifierIds : [];
        const modifiersLabel = item.modifiersLabel || '';
        const modifierIdsKey = JSON.stringify(modifierIds);

        this.setState((prev) => {
            const rows = prev.stageFields[F.MENU_ITEMS].ContainerFields;

            const existingIndex = rows.findIndex(
                (row) =>
                    row[F.MI_ITEM].FieldValue === item.id &&
                    JSON.stringify(row[F.MI_MODIFIER_SEL]?.FieldValue || []) === modifierIdsKey
            );

            let stageFields;
            if (existingIndex > -1) {
                const currentQty = Number(rows[existingIndex][F.MI_QTY].FieldValue || 0);
                const addQty = item.qty || 1;
                stageFields = updateContainerFieldValue(
                    prev.stageFields, F.MENU_ITEMS, existingIndex, F.MI_QTY, currentQty + addQty
                );
            } else {
                const row = buildMenuItemRow();
                row[F.MI_ITEM] = { ...row[F.MI_ITEM], FieldValue: item.id };
                row[F.MI_RATE] = { ...row[F.MI_RATE], FieldValue: item.price };
                row[F.MI_QTY] = { ...row[F.MI_QTY], FieldValue: item.qty || 1 };
                row[F.MI_KITCHEN] = { ...row[F.MI_KITCHEN], FieldValue: item.kitchen || '' };
                row[F.MI_UOM] = { ...row[F.MI_UOM], FieldValue: item.uom || '' };
                row[F.MI_KOT_STATUS] = { ...row[F.MI_KOT_STATUS], FieldValue: 'Pending' };
                row[F.MI_MODIFIER_SEL] = { ...row[F.MI_MODIFIER_SEL], FieldValue: modifierIds };
                row.__displayName = item.name;
                row.__modifiersLabel = modifiersLabel;
                stageFields = addContainerRow(prev.stageFields, F.MENU_ITEMS, row);
            }

            return { stageFields };
        });
    };

    updateQty = (rowIndex, delta) => {
        this.setState((prev) => {
            const rows = prev.stageFields[F.MENU_ITEMS].ContainerFields;
            const row = rows[rowIndex];
            const newQty = Number(row[F.MI_QTY].FieldValue || 0) + delta;

            const stageFields = newQty <= 0
                ? removeContainerRow(prev.stageFields, F.MENU_ITEMS, rowIndex)
                : updateContainerFieldValue(prev.stageFields, F.MENU_ITEMS, rowIndex, F.MI_QTY, newQty);

            return { stageFields };
        });
    };

    removeFromCart = (rowIndex) => {
        this.setState((prev) => ({
            stageFields: removeContainerRow(prev.stageFields, F.MENU_ITEMS, rowIndex),
        }));
    };

    // Absolute set (typing a quantity directly), as opposed to updateQty's
    // relative +/-1 nudge. Same "0 or less removes the row" convention.
    setCartQty = (rowIndex, qty) => {
        const newQty = Math.max(0, parseInt(qty, 10) || 0);
        this.setState((prev) => ({
            stageFields: newQty <= 0
                ? removeContainerRow(prev.stageFields, F.MENU_ITEMS, rowIndex)
                : updateContainerFieldValue(prev.stageFields, F.MENU_ITEMS, rowIndex, F.MI_QTY, newQty),
        }));
    };

    buildCartItems = () => {
        const rows = this.state.stageFields[F.MENU_ITEMS].ContainerFields;
        return rows.map((row, idx) => ({
            id: `${row[F.MI_ITEM]?.FieldValue}__${idx}`,
            name: row.__displayName || 'Unknown',
            price: Number(row[F.MI_RATE]?.FieldValue || 0),
            qty: Number(row[F.MI_QTY]?.FieldValue || 0),
            modifiersLabel: row.__modifiersLabel || '',
            __rowIndex: idx,
        }));
    };

    getGrandTotal = () => {
        const rows = this.state.stageFields[F.MENU_ITEMS].ContainerFields;
        return rows.reduce((sum, r) => {
            const qty = Number(r[F.MI_QTY].FieldValue) || 0;
            const rate = Number(r[F.MI_RATE].FieldValue) || 0;
            return sum + (qty * rate);
        }, 0);
    };

    // Takeaway is collected from the restaurant, so it needs no address —
    // the date and time stand for the pickup slot instead. Delivery stays
    // the default (set in afterAuthReady).
    isTakeaway = () =>
        this.state.stageFields?.[F.ORDER_TYPE]?.FieldValue === ORDER_TYPES.TAKEAWAY;

    setOrderType = (orderType) => this.setFlatField(F.ORDER_TYPE, orderType);

    // ── Gating ───────────────────────────────────────────────────────────────
    canPlaceOrder = () => {
        const { stageFields } = this.state;
        if (!stageFields) return false;

        const rows = stageFields[F.MENU_ITEMS].ContainerFields;
        if (rows.length === 0) return false;

        const custRow = stageFields[F.CUSTOMER_INFO].ContainerFields[0];
        const name = custRow[F.CUST_NAME]?.FieldValue;
        const mobile = (custRow[F.CUST_MOBILE]?.FieldValue || '').replace(/\s|-/g, '');
        const addressRow = stageFields[F.DELIVERY_ADDRESS].ContainerFields[0];
        const address = addressRow[F.DEL_ADDRESS]?.FieldValue;
        const deliveryDate = stageFields[F.DELIVERY_DATE]?.FieldValue;
        const expectedTime = stageFields[F.EXPECTED_DELIVERY_TIME]?.FieldValue;

        if (!name || !name.trim()) return false;
        if (!/^\d{10}$/.test(mobile)) return false;
        if (!this.isTakeaway() && (!address || !address.trim())) return false;
        if (!deliveryDate) return false;
        if (!expectedTime || !String(expectedTime).trim()) return false;

        const dateObj = new Date(deliveryDate);
        if (isNaN(dateObj.getTime()) || dateObj < this.getTomorrow()) return false;

        return true;
    };

    // Mirrors canPlaceOrder's checks but returns *why*, so a disabled Place
    // Order button isn't a silent dead end for the customer (or for anyone
    // debugging "nothing happens when I click Place Order" — a disabled
    // button never fires onClick at all, so no request, no console error,
    // nothing, unless something actually points at the missing field).
    getValidationMessage = () => {
        const { stageFields } = this.state;
        if (!stageFields) return null;

        const rows = stageFields[F.MENU_ITEMS].ContainerFields;
        if (rows.length === 0) return 'Add at least one item to your order.';

        const custRow = stageFields[F.CUSTOMER_INFO].ContainerFields[0];
        const name = custRow[F.CUST_NAME]?.FieldValue;
        const mobile = (custRow[F.CUST_MOBILE]?.FieldValue || '').replace(/\s|-/g, '');
        const addressRow = stageFields[F.DELIVERY_ADDRESS].ContainerFields[0];
        const address = addressRow[F.DEL_ADDRESS]?.FieldValue;
        const deliveryDate = stageFields[F.DELIVERY_DATE]?.FieldValue;
        const expectedTime = stageFields[F.EXPECTED_DELIVERY_TIME]?.FieldValue;

        const takeaway = this.isTakeaway();

        if (!name || !name.trim()) return 'Enter your name.';
        if (!/^\d{10}$/.test(mobile)) return 'Enter a valid 10-digit mobile number.';
        if (!takeaway && (!address || !address.trim())) return 'Enter your delivery address.';
        if (!deliveryDate) return takeaway ? 'Choose a pickup date.' : 'Choose a delivery date.';
        if (!expectedTime || !String(expectedTime).trim()) {
            return takeaway ? 'Choose a pickup time.' : 'Choose an expected delivery time.';
        }

        const dateObj = new Date(deliveryDate);
        if (isNaN(dateObj.getTime()) || dateObj < this.getTomorrow()) {
            return takeaway
                ? 'Pickup date must be tomorrow or later.'
                : 'Delivery date must be tomorrow or later.';
        }

        return null;
    };

    // ── RequestActions building ─────────────────────────────────────────────
    // The action/status flow master. Its Id/ActionId/StatusId/Sequence values
    // are sent straight through in every action row, so they have to match
    // the backend's own RESSTATUS master exactly — hence fetched from it
    // rather than transcribed into the UI the way QrOrderDetails.js/
    // RestaurantOrder.js still do: their hardcoded copies have already
    // drifted from it (both call KOT/15 "CLOSED", while the live master has
    // 15 = CONFIRMED BY CUSTOMER and CLOSED renumbered to 1999).
    // Deliberately a direct axios
    // call, not the getMasterData redux action: that one caches every master
    // in localStorage indefinitely, and a customer device holding a stale
    // copy of *this* list would silently save orders with the wrong
    // ResStatusId/Sequence.
    fetchResStatusMaster = () => {
        axios.get(
            '/master/api/res/GetresActionStatusflow?companyId=' + this.props.loggedUserDetail?.CompanyId
        )
            .then((response) => {
                const rows = Array.isArray(response.data) ? response.data : [];
                this.setState({
                    resStatusMaster: rows
                        .filter((r) => r.isActive !== false)
                        // camelCase from the API → the PascalCase keys the
                        // action rows are built with below.
                        .map((r) => ({
                            Id: r.id,
                            ActionId: r.actionid,
                            ActionName: (r.actionname || '').trim(),
                            StatusId: r.statusid,
                            StatusName: (r.statusName || '').trim(),
                            Sequence: r.sequence,
                        })),
                });
            })
            .catch(() => this.setState({ resStatusMaster: [] }));
    };

    // Looked up by name, not by StatusId: the numbers get renumbered on the
    // backend (KOT CLOSED went 15 → 1999), the names don't. ActionName scopes
    // it so ORDER/CONFIRMED can't collide with TABLERESERVATION/CONFIRMED.
    _resolveStatus = (actionName, statusName) => {
        const same = (a, b) => (a || '').trim().toUpperCase() === (b || '').trim().toUpperCase();
        return (this.state.resStatusMaster || []).find(
            (r) => same(r.ActionName, actionName) && same(r.StatusName, statusName)
        ) || null;
    };

    // deliveryDate is separate from targetDate on purpose: targetDate is when
    // this action happened (now), deliveryDate is when the customer asked for
    // the order. They only coincide on a same-day order. It defaults to
    // targetDate so a caller with no delivery date at all still gets a value.
    _buildBaseActionRow = (actionNum, resStatusRow, targetDate, deliveryDate = targetDate) => ({
        Actionnum: actionNum,
        ActionId: resStatusRow.ActionId,
        actiontype: resStatusRow.ActionId,
        ActionName: resStatusRow.ActionName,
        ResStatusId: resStatusRow.Id,
        StatusId: resStatusRow.StatusId,
        StatusName: resStatusRow.StatusName,
        Sequence: resStatusRow.Sequence,
        ActionDescription: resStatusRow.ActionName,
        ActionCode: resStatusRow.ActionName,
        Targetdate: moment(targetDate).toISOString(),
        // Date this action's order is meant to be delivered/collected on
        // (MM/DD/YYYY, no time). Used by the Captain Dashboard to filter KOTs.
        DeliveryDate: moment(deliveryDate).format('MM/DD/YYYY'),
        companyId: this.props.loggedUserDetail?.CompanyId,
        pqeacr: null,
        pqejrer: null,
        AssignedTo: null,
        AssignedByEmployeeId: null,
        DeligateToEmployeeId: null,
        DelicateToEmpEmailId: null,
        DeligateFromDate: null,
        DeligateToDate: null,
        Comments: null,
        OrderType: null,
        Branch: null,
        Waiter: null,
        Cashier: null,
        Shift: null,
        TokenNumber: null,
        BillNumber: null,
        TableNumber: null,
        TableId: null,
        TableSubCode: null,
        AdultCount: null,
        ChildCount: null,
        ReservationNumber: null,
        TableStatus: null,
        CustomerName: null,
        CustomerMobileNumber: null,
        CustomerMembershipNo: null,
        CustomerGstNo: null,
        CustomerRemarks: null,
        DeliveryAddress: null,
        DeliveryLandmark: null,
        DeliveryGoogleLocation: null,
        DeliveryBuilding: null,
        DeliveryFloor: null,
        DeliveryDoor: null,
        DeliveryBoy: null,
        DeliveryCharge: null,
        ExpectedDeliveryTime: null,
        Distance: null,
        DeliveryStatus: null,
        DeliveryInstruction: null,
        MenuId: null,
        KitchenId: '00000000-0000-0000-0000-000000000000',
        KOTNumber: null,
        KOTInstruction: null,
        ModifierId: null,
        MenuQty: null,
        ModifierQty: null,
        MenuUOM: null,
        ModifierUOM: null,
        MenuItemRate: null,
        MenuItemDiscountPercentage: null,
        MenuItemTaxPercentage: null,
        MenuItemNetAmount: null,
        MenuItemKitchenId: null,
        MenuItemSpecialInstruction: null,
        MenuItemKotStatus: null,
        MenuItemServedQty: null,
        MenuItemPreparedQty: null,
        MenuItemBalanceQty: null,
        MenuItemCancelledQty: null,
        BillHeaderGrossAmount: null,
        BillHeaderItemDiscount: null,
        BillHeaderBillDiscount: null,
        BillHeaderTax: null,
        BillHeaderServiceCharge: null,
        BillHeaderPackingCharge: null,
        BillHeaderDeliveryCharge: null,
        BillHeaderRoundOff: null,
        BillHeaderGrandTotal: null,
        PaymentType: null,
        PaymentAmountTendered: null,
        PaymentChangeReturn: null,
        PaymentTransactionNumber: null,
        PaymentCardNumber: null,
        PaymentBank: null,
        PaymentApprovalNo: null,
        PaymentStatus: null,
    });

    _buildOrderHeaderFields = (stageFields) => {
        const customerInfo = stageFields[F.CUSTOMER_INFO]?.ContainerFields?.[0];
        const deliveryAddress = stageFields[F.DELIVERY_ADDRESS]?.ContainerFields?.[0];
        let orderType = '';
        Object.values(stageFields).forEach((field) => {
            if (field.FieldName === raw(F.ORDER_TYPE)) orderType = field.FieldValue;
        });
        return {
            OrderType: orderType || null,
            Branch: this.props.loggedUserDetail?.defaultbranch || '00000000-0000-0000-0000-000000000001',
            CustomerName: customerInfo?.[F.CUST_NAME]?.FieldValue || null,
            CustomerMobileNumber: customerInfo?.[F.CUST_MOBILE]?.FieldValue || null,
            CustomerMembershipNo: customerInfo?.[F.CUST_MEMBERSHIP_NO]?.FieldValue || null,
            CustomerGstNo: customerInfo?.[F.CUST_GST]?.FieldValue || null,
            CustomerRemarks: customerInfo?.[F.CUST_REMARKS]?.FieldValue || null,
            DeliveryAddress: deliveryAddress?.[F.DEL_ADDRESS]?.FieldValue || null,
            DeliveryLandmark: deliveryAddress?.[F.DEL_LANDMARK]?.FieldValue || null,
            DeliveryGoogleLocation: deliveryAddress?.[F.DEL_GOOGLE_LOC]?.FieldValue || null,
            DeliveryBuilding: deliveryAddress?.[F.DEL_BUILDING]?.FieldValue || null,
            DeliveryFloor: deliveryAddress?.[F.DEL_FLOOR]?.FieldValue || null,
            DeliveryDoor: deliveryAddress?.[F.DEL_DOOR_NO]?.FieldValue || null,
        };
    };

    _buildBillHeaderFields = (stageFields) => {
        const b = stageFields[F.BILL_SUMMARY]?.ContainerFields?.[0];
        if (!b) return {};
        return {
            BillHeaderGrossAmount: b[F.BILL_GROSS]?.FieldValue ?? null,
            BillHeaderItemDiscount: b[F.BILL_ITEM_DISCOUNT]?.FieldValue ?? null,
            BillHeaderBillDiscount: b[F.BILL_DISCOUNT]?.FieldValue ?? null,
            BillHeaderTax: b[F.BILL_TAX]?.FieldValue ?? null,
            BillHeaderServiceCharge: b[F.BILL_SERVICE_CHG]?.FieldValue ?? null,
            BillHeaderPackingCharge: b[F.BILL_PACKING_CHG]?.FieldValue ?? null,
            BillHeaderDeliveryCharge: b[F.BILL_DELIVERY_CHG]?.FieldValue ?? null,
            BillHeaderRoundOff: b[F.BILL_ROUND_OFF]?.FieldValue ?? null,
            BillHeaderGrandTotal: b[F.BILL_GRAND_TOTAL]?.FieldValue ?? null,
        };
    };

    // Three action rows/groups in this one save: an order-level ORDER/
    // CONFIRMED row, one KOT/CONFIRMED BY CUSTOMER row per cart item (same
    // shape QrOrderDetails.confirmOrder's 'confirm' branch sends), and one
    // order-level DELIVERY row carrying the concatenated delivery date+time.
    buildRequestActions = (syncedStageFields) => {
        const orderHeader = this._buildOrderHeaderFields(syncedStageFields);
        const billFields = this._buildBillHeaderFields(syncedStageFields);
        const now = new Date();
        // Customer's chosen delivery/pickup date for this special order — falls
        // back to now when not set. Goes onto EVERY action row, not just the KOT
        // ones: the Captain Dashboard filters KOTs by it, and the ORDER and
        // DELIVERY rows were being stamped with the action's own date (today)
        // instead, so a 12th-of-the-month order for the 15th filed its own
        // delivery action under the 12th.
        const deliveryDate = syncedStageFields[F.DELIVERY_DATE]?.FieldValue || now;

        const confirmedStatus = this._resolveStatus('ORDER', 'CONFIRMED');
        const orderRow = {
            ...this._buildBaseActionRow(1, confirmedStatus, now, deliveryDate),
            ...orderHeader,
            ...billFields,
        };

        const kotStatus = this._resolveStatus('KOT', KOT_CUSTOMER_CONFIRMED);
        const menuRows = syncedStageFields[F.MENU_ITEMS]?.ContainerFields || [];
        const kotRows = menuRows.map((row, idx) => {
            const modifierIds = this.parseModifierIds(row[F.MI_MODIFIER_SEL]?.FieldValue);
            return {
                ...this._buildBaseActionRow(idx + 2, kotStatus, now, deliveryDate),
                ...orderHeader,
                ...billFields,
                pqejrer: 'NEW',
                MenuId: row[F.MI_ITEM]?.FieldValue || null,
                KitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                MenuItemKitchenId: row[F.MI_KITCHEN]?.FieldValue || null,
                MenuQty: row[F.MI_QTY]?.FieldValue ?? null,
                MenuUOM: row[F.MI_UOM]?.FieldValue || null,
                MenuItemRate: row[F.MI_RATE]?.FieldValue ?? null,
                MenuItemDiscountPercentage: row[F.MI_DISCOUNT_PCT]?.FieldValue ?? null,
                MenuItemTaxPercentage: row[F.MI_TAX_PCT]?.FieldValue ?? null,
                // Stored Net Amount when the row carries one, else qty x rate.
                // Number() on every part for two reasons: a rehydrated row holds
                // strings, where a stored "0" is truthy and would short-circuit
                // the fallback; and an absent field gives NaN, which is falsy and
                // correctly falls through. The || chain always yields a number,
                // so there is nothing left for a ?? to catch.
                MenuItemNetAmount: Number(row[F.MI_NET_AMOUNT]?.FieldValue)
                    || (Number(row[F.MI_QTY]?.FieldValue) || 0) * (Number(row[F.MI_RATE]?.FieldValue) || 0),
                MenuItemSpecialInstruction: row[F.MI_SPECIAL_INSTR]?.FieldValue || null,
                MenuItemKotStatus: row[F.MI_KOT_STATUS]?.FieldValue || null,
                MenuItemServedQty: row[F.MI_SERVED_QTY]?.FieldValue ?? null,
                MenuItemPreparedQty: row[F.MI_PREPARED_QTY]?.FieldValue ?? null,
                MenuItemBalanceQty: row[F.MI_BALANCE_QTY]?.FieldValue ?? null,
                MenuItemCancelledQty: row[F.MI_CANCELLED_QTY]?.FieldValue ?? null,
                ModifierId: modifierIds.length ? modifierIds : null,
            };
        });

        const expectedTime = syncedStageFields[F.EXPECTED_DELIVERY_TIME]?.FieldValue;
        const expectedDeliveryTime = `${moment(deliveryDate).format('MM/DD/YYYY')} ${expectedTime || ''}`.trim();

        const deliveryRow = {
            ...this._buildBaseActionRow(menuRows.length + 2, DELIVERY_SCHEDULED_STATUS, now, deliveryDate),
            ...orderHeader,
            ...billFields,
            ExpectedDeliveryTime: expectedDeliveryTime,
        };

        // Online payment: an order-level PAYMENT/PENDING row goes up with the
        // order itself. The backend moves it on once Razorpay confirms the
        // payment, and the Razorpay order carries this request's id to match.
        const paymentRows = [];
        if (this.state.paymentMethod === 'online') {
            const paymentPending = this._resolveStatus('PAYMENT', PAYMENT_PENDING);
            paymentRows.push({
                ...this._buildBaseActionRow(menuRows.length + 3, paymentPending, now, deliveryDate),
                ...orderHeader,
                ...billFields,
                PaymentType: ONLINE_PAYMENT_TYPE,
                PaymentAmountTendered: billFields.BillHeaderGrandTotal ?? null,
                PaymentStatus: paymentPending.StatusName,
            });
        }

        return [orderRow, ...kotRows, deliveryRow, ...paymentRows];
    };

    // ── RequestAttributes building ──────────────────────────────────────────
    // Always a first-and-only save (no requestId ever exists for this page),
    // so this always sends the full field set — same "isInitialEntry" path
    // QrOrderDetails.js's own buildGroupAttributes takes, just without the
    // isUpdate/FieldId branch since there's never an update here.
    buildGroupAttributes = (syncedStageFields) => {
        const groupAttributes = [];
        let fieldSequence = 1;

        const serializeFieldValue = (fieldValue) => {
            if (fieldValue === null || fieldValue === undefined) return fieldValue;
            if (Array.isArray(fieldValue)) return JSON.stringify(fieldValue);
            if (typeof fieldValue === 'object') return JSON.stringify(fieldValue);
            return fieldValue;
        };

        Object.entries(syncedStageFields).forEach(([, field]) => {
            groupAttributes.push({
                ChildRowNmbr: field.ChildRowNmbr,
                DataType: field.DataType,
                FieldDescription: field.FieldLabel,
                FieldName: field.FieldName,
                FieldType: field.FieldType,
                FieldValue: serializeFieldValue(
                    (field.FieldType === 7 || field.FieldType === 8)
                        ? moment(field.FieldValue).format('MM/DD/YYYY')
                        : field.FieldValue
                ),
                ParentFieldId: field.ParentFieldId,
                stageNum: STAGE_NUM,
                processNum: PROCESS_NUM,
                FieldSequence: fieldSequence++,
                IsActive: true,
                CompanyID: this.props.loggedUserDetail?.CompanyId,
                StageSequence: 0,
                ContainerType: field.type,
                branch: '00000000-0000-0000-0000-000000000001',
            });

            if (field.ContainerFields === null) return;

            Object.entries(field.ContainerFields).forEach(([, containerField], containerIndex) => {
                Object.entries(containerField)
                    .filter(([, f]) => f && typeof f === 'object' && f.FieldName)
                    .forEach(([, containerFField]) => {
                        groupAttributes.push({
                            ChildRowNmbr: containerIndex + 1,
                            DataType: containerFField.DataType,
                            FieldDescription: containerFField.FieldLabel,
                            FieldName: containerFField.FieldName,
                            FieldType: containerFField.FieldType,
                            FieldValue: serializeFieldValue(
                                (containerFField.FieldType === 7 || containerFField.FieldType === 8)
                                    ? moment(containerFField.FieldValue).format('MM/DD/YYYY')
                                    : containerFField.FieldValue
                            ),
                            ParentFieldId: containerFField.ParentFieldId,
                            stageNum: STAGE_NUM,
                            processNum: PROCESS_NUM,
                            FieldSequence: fieldSequence++,
                            IsActive: true,
                            CompanyID: this.props.loggedUserDetail?.CompanyId,
                            StageSequence: 0,
                            ContainerType: field.type,
                        });
                    });
            });
        });

        return groupAttributes;
    };

    // ── Place Order ──────────────────────────────────────────────────────────
    // Two-step chain: create the customer employee record first (called
    // directly via axios, not the fire-and-forget processDynamicData redux
    // action, since RequestedFor needs the real generated id back), then
    // save the order using that id.
    placeOrder = () => {
        if (this.state.isSaving || !this.canPlaceOrder()) return;

        // Checked before the employee insert, not inside saveOrder: without
        // both rows the action rows can't be built at all, and failing after
        // that first call would leave an orphan customer record behind for an
        // order that never got saved.
        if (!this._resolveStatus('ORDER', 'CONFIRMED') || !this._resolveStatus('KOT', KOT_CUSTOMER_CONFIRMED)
            || (this.state.paymentMethod === 'online' && !this._resolveStatus('PAYMENT', PAYMENT_PENDING))) {
            this.growl && this.growl.show({
                severity: 'error',
                summary: null,
                detail: this.state.resStatusMaster === null
                    ? 'Still getting things ready — please try again in a moment.'
                    : 'Order statuses are unavailable right now. Please try again later.',
            });
            return;
        }

        this.setState({ isSaving: true });

        const { stageFields } = this.state;
        const custRow = stageFields[F.CUSTOMER_INFO].ContainerFields[0];
        const name = custRow[F.CUST_NAME].FieldValue.trim();
        const phone = custRow[F.CUST_MOBILE].FieldValue.trim();

        const employeeData = {
            Employee: {
                EmployeeName: name,
                FirstName: name.split(' ')[0],
                LastName: name.split(' ').slice(1).join(' ') || null,
                EmployeeTypeId: null,
                EmployeeNo: phone,
                MobileNo: phone,
                IsActive: true,
                CompanyId: this.props.loggedUserDetail?.CompanyId,
                CreatedDate: new Date(),
                Createdby: name,
                BankSamba: '108',
            },
            EmployeePosition: [{
                PositionId: 'B521A370-BD4E-4641-AA7C-222181337770',
                UncId: null,
                IsUnc: false,
                SectionId: null,
                IsSection: false,
                DepartmentId: '9B7F1501-8DF5-4F3C-924E-6541EF946ED7',
                IsDepartment: true,
                DivisionId: null,
                IsDivision: false,
                VpUnitId: null,
                IsVpUnit: false,
                ExecutiveUnitId: null,
                IsExecutiveUnit: false,
                EmployeeLevelId: null,
                Isactive: true,
                Createdby: name,
                Createddate: new Date(),
                Updatedby: name,
                Updateddate: new Date(),
                CompanyId: this.props.loggedUserDetail?.CompanyId,
            }],
            EmployeeDocuments: [],
            EmployeeDependant: [],
            EmployeePo: [],
            EmployeeLeaveTypeQuota: [],
            EmployeeTicketQuota: [],
            EmployeeVisaQuota: [],
            EmployeeAllowance: [],
        };

        axios.post(
            '/master/api/InsertDynamicData?model=EmployeeRes',
            JSON.stringify({ Key: JSON.stringify(employeeData) })
        )
            .then((response) => {
                const requestedForId =
                    response.data?.requestId || response.data?.requestID || response.data?.id;

                if (!requestedForId || requestedForId === '00000000-0000-0000-0000-000000000000') {
                    throw new Error('Customer record was not created.');
                }
                this.saveOrder(requestedForId);
            })
            .catch((err) => {
                this.setState({ isSaving: false });
                this.growl && this.growl.show({
                    severity: 'error',
                    summary: null,
                    detail: (err && err.response && err.response.data) || err.message || 'Could not create customer record',
                });
            });
    };

    saveOrder = (requestedForId) => {
        const { stageFields } = this.state;
        const total = this.getGrandTotal();
        const syncedStageFields = updateContainerFieldValue(
            stageFields, F.BILL_SUMMARY, 0, F.BILL_GRAND_TOTAL, total
        );

        let RequestType = '';
        Object.values(syncedStageFields).forEach((field) => {
            if (field.FieldName === raw(F.ORDER_TYPE)) RequestType = field.FieldValue;
        });

        const requestAttributes = this.buildGroupAttributes(syncedStageFields);
        const requestActions = this.buildRequestActions(syncedStageFields);

        const now = new Date();
        const deliveryDate = syncedStageFields[F.DELIVERY_DATE]?.FieldValue || now ;

        // Same accessors the action rows use, so the header copies below can
        // never disagree with what Requestactions carries for the same order.
        const orderHeader = this._buildOrderHeaderFields(syncedStageFields);
        const paymentInfo = syncedStageFields[F.PAYMENT_INFO]?.ContainerFields?.[0];

        const data = {
            RequestDesc: 'Restaurant Order',
            IsActive: true,
            RequestDate: moment(now).format('MM/DD/YYYY'),
            FromDate: moment(deliveryDate).format('MM/DD/YYYY'),
            ToDate: moment(deliveryDate).format('MM/DD/YYYY'),
            EmpID: this.props.loggedUserDetail?.empID,
            CompanyID: this.props.loggedUserDetail?.CompanyId,
            Remark: '',
            RequestedFor: requestedForId,
            RequestType: RequestType,
            ProcessID: RESTAURANT_ORDER_ID.toUpperCase(),

            // ── Restaurant order header (REQUESTHEADER Res* columns) ──────────
            // Denormalised copies of values that otherwise live only inside the
            // attribute containers, so an order can be listed and reported on
            // without unpacking RequestAttributes. Resdeliverydate goes up in
            // the same MM/DD/YYYY the sibling RequestDate/FromDate/ToDate use.
            // From state, set by the delivery-date picker. Guarded because
            // moment('') / moment(null) format to "Invalid date".
            resdeliverydate: this.state.resdeliverydate
                ? moment(this.state.resdeliverydate).format('MM/DD/YYYY')
                : '',
            Resdeliverytime: syncedStageFields[F.EXPECTED_DELIVERY_TIME]?.FieldValue || '',
            Resdeliveryaddress: orderHeader.DeliveryAddress || '',
            Rescustomername: orderHeader.CustomerName || '',
            Resphone: orderHeader.CustomerMobileNumber || '',
            Restotalamount: total,
            // Nothing on this page takes payment — a special order is placed,
            // not paid — so this is whatever Payment Information holds, which
            // stays empty until the order is settled in the POS.
            Respaymentstatus: paymentInfo?.[F.PAY_STATUS]?.FieldValue || '',

            RequestAttributes: requestAttributes,
            Requestattachments: [],
            Requestforms: [],
            Requestactions: requestActions,
            StageSequence: 0,
        };

        axios.post(
            '/master/api/InsertDynamicData?model=requestSave',
            JSON.stringify({ Key: JSON.stringify(data) })
        )
            .then((response) => {
                const saved = response.data || {};
                const requestId = saved.requestId || saved.requestID || saved.requestid || saved.id || null;

                const payOnline = this.state.paymentMethod === 'online';

                this.setState({
                    isSaving: false,
                    orderPlaced: true,
                    orderNumber: saved.requestNum || null,
                    savedRequestId: requestId,
                    savedTotal: total,
                }, () => {
                    // The order is saved first, then paid for: a failed or
                    // cancelled payment leaves an unpaid order that can be
                    // retried from the confirmation screen, without inserting
                    // the order a second time.
                    if (payOnline) this.startOnlinePayment();
                });

                // requestSave's insert response only hands back the new
                // request's GUID — the human-readable order number is
                // generated server-side and only appears on the request
                // itself, so it takes a follow-up read (the requestNum above
                // is just in case that ever changes). The confirmation screen
                // is already up by then; the number fills itself in.
                if (!saved.requestNum && requestId) this.fetchOrderNumber(requestId);
            })
            .catch((err) => {
                this.setState({ isSaving: false });
                this.growl && this.growl.show({
                    severity: 'error',
                    summary: null,
                    detail: (err && err.response && err.response.data) || err.message || 'Failed to place order',
                });
            });
    };

    // ── Online payment (Razorpay) ───────────────────────────────────────────
    // Opens Razorpay Checkout for the saved order's total, using the same
    // /create-order and /verify-payment calls as RestaurantOrder's
    // payWithRazorpay. Reads the order from state (set by saveOrder), so the
    // confirmation screen's "Pay Now" can call it again after a failure.
    startOnlinePayment = () => {
        const { savedRequestId, savedTotal, orderNumber, stageFields } = this.state;
        if (!savedTotal || this.state.onlinePaymentStatus === 'processing') return;

        const custRow = stageFields?.[F.CUSTOMER_INFO]?.ContainerFields?.[0] || {};
        const prefill = {
            name: custRow[F.CUST_NAME]?.FieldValue || '',
            contact: custRow[F.CUST_MOBILE]?.FieldValue || '',
        };

        this.setState({ onlinePaymentStatus: 'processing' });

        // The test API takes the amount in rupees and converts it to paise
        // itself, so it isn't multiplied by 100 here.
        razorpayAxios.post('/create-order', {
            amount: savedTotal,
            currency: 'INR',
            notes: {
                orderNumber: orderNumber || '',
                requestId: savedRequestId || '',
            },
        })
            .then(({ data }) => {
                // Same two response shapes RestaurantOrder accepts.
                const order = data.order || data;
                const keyId = data.key_id || order.key_id;

                openRazorpayCheckout({
                    order,
                    keyId,
                    name: 'Chenthoor',
                    description: `Special Order ${this.state.orderNumber || ''}`.trim(),
                    prefill,
                    onSuccess: (response) => {
                        razorpayAxios.post('/verify-payment', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        })
                            .then(({ data: verification }) => {
                                this.setState({
                                    onlinePaymentStatus: verification && verification.verified ? 'paid' : 'failed',
                                });
                            })
                            .catch(() => this.setState({ onlinePaymentStatus: 'failed' }));
                    },
                    onFailure: (err) => {
                        this.setState({
                            onlinePaymentStatus: err && err.message === 'Payment cancelled' ? 'cancelled' : 'failed',
                        });
                    },
                });
            })
            .catch(() => this.setState({ onlinePaymentStatus: 'failed' }));
    };

    renderOnlinePaymentStatus = () => {
        const { onlinePaymentStatus, savedTotal } = this.state;
        if (!onlinePaymentStatus) return null;

        if (onlinePaymentStatus === 'processing') {
            return <p className="so-confirm__payment">Processing your payment…</p>;
        }
        if (onlinePaymentStatus === 'paid') {
            return (
                <p className="so-confirm__payment so-confirm__payment--paid">
                    <i className="pi pi-check-circle" /> Payment of ₹{Number(savedTotal).toFixed(2)} received.
                </p>
            );
        }
        return (
            <div className="so-confirm__payment so-confirm__payment--pending">
                <p>
                    {onlinePaymentStatus === 'cancelled'
                        ? 'Payment was not completed.'
                        : 'Payment failed.'}
                    {' '}Your order is saved. You can try paying again.
                </p>
                <button type="button" className="so-order-btn" onClick={this.startOnlinePayment}>
                    Pay ₹{Number(savedTotal).toFixed(2)} <i className="pi pi-credit-card" />
                </button>
            </div>
        );
    };

    // ── Render ──────────────────────────────────────────────────────────────
    renderOfferCard = (item, idx) => {
        const cartRow = this.findCartRowForItem(item.menuItemId);
        const imageUrl = this.getMenuImageUrl(item.photopath);

        return (
            <div className="so-offer-card" key={item.menuItemId}>
                <div className={`so-offer-thumb so-offer-thumb--${idx % 6}`}>
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={item.menuName}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    ) : (
                        <i className="pi pi-star-fill" />
                    )}
                </div>

                <div className="so-offer-body">
                    <p className="so-offer-name">{item.menuName}</p>
                    <span className="so-offer-price">₹{Number(item.sellingPrice || 0).toFixed(2)}</span>
                </div>

                <div className="so-offer-action">
                    {cartRow ? (
                        <div className="so-qty-stepper">
                            <button
                                type="button"
                                onClick={() => this.updateQty(cartRow.rowIndex, -1)}
                                aria-label={`Decrease ${item.menuName}`}
                            >
                                −
                            </button>
                            {/* Tapping the number (not +/-) opens the keypad
                                instead of turning into a plain text field. */}
                            <button
                                type="button"
                                className="so-qty-stepper__value"
                                onClick={() => this.openQtyKeypad(cartRow.rowIndex, item.menuName, cartRow.qty)}
                                aria-label={`Edit quantity for ${item.menuName}`}
                            >
                                {cartRow.qty}
                            </button>
                            <button
                                type="button"
                                onClick={() => this.updateQty(cartRow.rowIndex, 1)}
                                aria-label={`Increase ${item.menuName}`}
                            >
                                +
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="so-add-btn"
                            onClick={() => this.addToCart({
                                id: item.menuItemId,
                                name: item.menuName,
                                price: Number(item.sellingPrice || 0),
                                kitchen: item.defaultkitchen,
                                uom: item.baseunit,
                                qty: 1,
                            })}
                        >
                            ADD
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Horizontally scrollable category chips. Hidden when there's only one
    // stocked category — a lone "tab" implies a choice that doesn't exist.
    renderCategoryTabs = () => {
        const categories = this.getBrowsableCategories();
        if (categories.length < 2) return null;

        const { selectedCategoryId } = this.state;

        return (
            <div className="so-cat-tabs" role="tablist" aria-label="Menu categories">
                {categories.map((category) => {
                    const isActive = category.value === selectedCategoryId;
                    return (
                        <button
                            type="button"
                            key={category.value}
                            role="tab"
                            aria-selected={isActive}
                            ref={isActive ? this.activeTabRef : null}
                            className={`so-cat-tab${isActive ? ' so-cat-tab--active' : ''}`}
                            onClick={() => this.selectCategory(category.value)}
                        >
                            {category.label}
                        </button>
                    );
                })}
            </div>
        );
    };

    // Delivery or takeaway. Rendered twice from here — once at the top of the
    // browse step, where the customer settles it before picking items, and
    // again in the checkout form so it's still changeable at the last step.
    // Both read and write the same ORDER_TYPE field, so they never disagree.
    // Takeaway drops the address field in checkout and relabels the date/time
    // as the pickup slot; the order rows sent on save are otherwise identical.
    renderOrderTypeField = () => {
        const takeaway = this.isTakeaway();

        return (
            <div className="so-field">
                <label><i className="pi pi-shopping-bag" />Order Type</label>
                <div className="so-ordertype">
                    <button
                        type="button"
                        className={`so-ordertype__btn${!takeaway ? ' so-ordertype__btn--active' : ''}`}
                        onClick={() => this.setOrderType(ORDER_TYPES.DELIVERY)}
                    >
                        <i className="pi pi-send" />Delivery
                    </button>
                    <button
                        type="button"
                        className={`so-ordertype__btn${takeaway ? ' so-ordertype__btn--active' : ''}`}
                        onClick={() => this.setOrderType(ORDER_TYPES.TAKEAWAY)}
                    >
                        <i className="pi pi-shopping-bag" />Takeaway
                    </button>
                </div>
            </div>
        );
    };

    renderSubheader = (title, onBack) => (
        <div className="so-subheader">
            <button type="button" className="so-subheader__back" onClick={onBack} aria-label="Back">
                <i className="pi pi-arrow-left" />
            </button>
            <h2 className="so-subheader__title">{title}</h2>
        </div>
    );

    // ── Step 1: Browse ───────────────────────────────────────────────────────
    renderBrowseStep = () => {
        const offerItems = this.getOfferItems();
        const categoryLabel = this.getCategoryLabel();
        const cartItems = this.buildCartItems();
        const grandTotal = this.getGrandTotal();
        const cartCount = cartItems.reduce((sum, i) => sum + i.qty, 0);
        const { searchQuery } = this.state;
        const filteredItems = searchQuery.trim()
            ? offerItems.filter((item) =>
                (item.menuName || '').toLowerCase().includes(searchQuery.trim().toLowerCase())
            )
            : offerItems;

        return (
            <>
                <div className="so-hero">
                    <img src={ChenthoorIcon} alt="" className="so-hero__watermark" />
                    <div className="so-hero__content">
                        <img src={ChenthoorLogo} alt="Chenthoor" className="so-hero__logo" />
                        <span className="so-hero__eyebrow">Exclusive Offers</span>
                        <h1 className="so-hero__title">{categoryLabel}</h1>
                        <div className="so-hero__rule"><span /></div>
                        <p className="so-hero__subtitle">Delicious food, special prices — order now for the date you want it.</p>
                    </div>
                </div>

                <div className="so-sheet">
                    {/* First thing under the hero: delivery or takeaway, so the
                        customer knows which they're ordering before they start
                        adding items. Still changeable at checkout. */}
                    <div className="so-ordertype-card">
                        {this.renderOrderTypeField()}
                    </div>

                    <h2 className="so-section-title">Choose Your Items</h2>

                    {this.renderCategoryTabs()}

                    {offerItems.length > 0 && (
                        <div className="so-search">
                            <i className="pi pi-search" />
                            <input
                                type="text"
                                placeholder="Search items…"
                                value={searchQuery}
                                onChange={(e) => this.setState({ searchQuery: e.target.value })}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    className="so-search__clear"
                                    onClick={() => this.setState({ searchQuery: '' })}
                                    aria-label="Clear search"
                                >
                                    <i className="pi pi-times" />
                                </button>
                            )}
                        </div>
                    )}

                    {filteredItems.length > 0 ? (
                        <div className="so-offer-list">
                            {filteredItems.map((item, idx) => this.renderOfferCard(item, idx))}
                        </div>
                    ) : (
                        <p className="so-empty-note">
                            {offerItems.length === 0
                                ? 'No items available in this category right now.'
                                : `No items match "${searchQuery}".`}
                        </p>
                    )}

                    <div className="so-feature-strip">
                        <div className="so-feature">
                            <i className="pi pi-send" />
                            <strong>Free Delivery</strong>
                            <span>No charges, ever</span>
                        </div>
                        <div className="so-feature">
                            <i className="pi pi-verified" />
                            <strong>Best Quality</strong>
                            <span>Fresh &amp; hygienic</span>
                        </div>
                        <div className="so-feature">
                            <i className="pi pi-calendar-plus" />
                            <strong>Scheduled Delivery</strong>
                            <span>Pick your own date</span>
                        </div>
                        <div className="so-feature">
                            <i className="pi pi-headphones" />
                            <strong>24/7 Support</strong>
                            <span>We're always here</span>
                        </div>
                    </div>
                </div>

                {/* A guest lands here signed out, so this step is the whole site
                    to them — it ends with a proper footer. Only on this step:
                    the cart and checkout steps are a task to finish, with the
                    order bar at the foot of the screen. */}
                <SpecialOrderFooter />

                {/* Only appears once something's in the cart. Tapping
                    anywhere on the bar (not just a small button) opens the
                    cart review — a plain nav action, not a submit. */}
                {cartItems.length > 0 && (
                    <div className="so-order-bar">
                        <button type="button" className="so-order-bar__tap" onClick={this.goToCart}>
                            <div className="so-order-bar__inner">
                                <div className="so-order-bar__row">
                                    <div className="so-order-bar__summary">
                                        {cartCount} item{cartCount !== 1 ? 's' : ''}
                                        <strong>₹{grandTotal.toFixed(2)}</strong>
                                    </div>
                                    <span className="so-order-btn">
                                        View Cart <i className="pi pi-chevron-right" />
                                    </span>
                                </div>
                            </div>
                        </button>
                    </div>
                )}
            </>
        );
    };

    // ── Step 2: Cart review ──────────────────────────────────────────────────
    renderCartStep = () => {
        const cartItems = this.buildCartItems();
        const grandTotal = this.getGrandTotal();
        const cartCount = cartItems.reduce((sum, i) => sum + i.qty, 0);

        if (cartItems.length === 0) {
            return (
                <>
                    {this.renderSubheader('Your Order', this.backToBrowse)}
                    <div className="so-empty-cart">
                        <p>Your cart is empty.</p>
                        <button type="button" onClick={this.backToBrowse}>Browse Offers</button>
                    </div>
                </>
            );
        }

        return (
            <>
                {this.renderSubheader('Your Order', this.backToBrowse)}

                <div className="so-cart-list">
                    {cartItems.map((item) => (
                        <div className="so-cart-row" key={item.id}>
                            <div className="so-cart-row__info">
                                <p className="so-cart-row__name">{item.name}</p>
                                <span className="so-cart-row__unit">₹{item.price.toFixed(2)} each</span>
                            </div>
                            <div className="so-cart-row__right">
                                <span className="so-cart-row__total">₹{(item.price * item.qty).toFixed(2)}</span>
                                <div className="so-qty-stepper">
                                    <button
                                        type="button"
                                        onClick={() => this.updateQty(item.__rowIndex, -1)}
                                        aria-label={`Decrease ${item.name}`}
                                    >
                                        −
                                    </button>
                                    <button
                                        type="button"
                                        className="so-qty-stepper__value"
                                        onClick={() => this.openQtyKeypad(item.__rowIndex, item.name, item.qty)}
                                        aria-label={`Edit quantity for ${item.name}`}
                                    >
                                        {item.qty}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => this.updateQty(item.__rowIndex, 1)}
                                        aria-label={`Increase ${item.name}`}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="so-summary-card">
                    <div className="so-summary-row so-summary-row--total">
                        <span>Total</span>
                        <span>₹{grandTotal.toFixed(2)}</span>
                    </div>
                </div>

                <div className="so-order-bar">
                    <div className="so-order-bar__inner">
                        <div className="so-order-bar__row">
                            <div className="so-order-bar__summary">
                                {cartCount} item{cartCount !== 1 ? 's' : ''}
                                <strong>₹{grandTotal.toFixed(2)}</strong>
                            </div>
                            <button type="button" className="so-order-btn" onClick={this.goToCheckout}>
                                Place Order <i className="pi pi-arrow-right" />
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    // ── Step 3: Checkout — details, line items, payment, confirm ────────────
    renderCheckoutStep = () => {
        const { stageFields, isSaving, paymentMethod } = this.state;
        const customerInfo = stageFields[F.CUSTOMER_INFO].ContainerFields[0];
        const deliveryAddress = stageFields[F.DELIVERY_ADDRESS].ContainerFields[0];
        const cartItems = this.buildCartItems();
        const grandTotal = this.getGrandTotal();
        const validationMessage = this.getValidationMessage();
        const takeaway = this.isTakeaway();

        return (
            <>
                {this.renderSubheader('Checkout', this.backToCart)}

                <div className="so-form-card">
                    {/* Chosen at the top of the browse step; repeated here so
                        it can still be switched without going back. */}
                    {this.renderOrderTypeField()}
                    <div className="so-field">
                        <label htmlFor="so-name"><i className="pi pi-user" />Name</label>
                        <input
                            id="so-name"
                            type="text"
                            value={customerInfo[F.CUST_NAME].FieldValue}
                            onChange={(e) => this.setContainerField(F.CUSTOMER_INFO, F.CUST_NAME, e.target.value)}
                        />
                    </div>
                    <div className="so-field">
                        <label htmlFor="so-mobile"><i className="pi pi-phone" />Mobile Number</label>
                        <input
                            id="so-mobile"
                            type="text"
                            maxLength={10}
                            value={customerInfo[F.CUST_MOBILE].FieldValue}
                            onChange={(e) => this.setContainerField(
                                F.CUSTOMER_INFO, F.CUST_MOBILE, e.target.value.replace(/\D/g, '')
                            )}
                        />
                    </div>
                    {!takeaway && (
                        <div className="so-field">
                            <label htmlFor="so-address"><i className="pi pi-map-marker" />Delivery Address</label>
                            <textarea
                                id="so-address"
                                rows={3}
                                value={deliveryAddress[F.DEL_ADDRESS].FieldValue}
                                onChange={(e) => this.setContainerField(F.DELIVERY_ADDRESS, F.DEL_ADDRESS, e.target.value)}
                            />
                        </div>
                    )}
                    {takeaway && (
                        <p className="so-pickup-note">
                            <i className="pi pi-info-circle" />
                            Collect your order from the restaurant at the time below.
                        </p>
                    )}
                    <div className="so-field-row">
                        <div className="so-field">
                            <label htmlFor="so-date">
                                <i className="pi pi-calendar" />{takeaway ? 'Pickup Date' : 'Delivery Date'}
                            </label>
                            <Calendar
                                inputId="so-date"
                                value={stageFields[F.DELIVERY_DATE].FieldValue}
                                onChange={(e) => {
                                    this.setFlatField(F.DELIVERY_DATE, e.value);
                                    this.setState({ resdeliverydate: e.value });
                                }}
                                minDate={this.getTomorrow()}
                                dateFormat="dd/mm/yy"
                                showIcon
                                readOnlyInput
                                placeholder="Select date"
                                appendTo={document.body}
                            />
                        </div>
                        <div className="so-field">
                            <label htmlFor="so-time">
                                <i className="pi pi-clock" />{takeaway ? 'Pickup Time' : 'Expected Time'}
                            </label>
                            {(() => {
                                const { hour12, minute, period } = this.getTimeParts(
                                    stageFields[F.EXPECTED_DELIVERY_TIME].FieldValue
                                );
                                return (
                                    <div className="so-time-picker" id="so-time">
                                        <select
                                            value={hour12}
                                            aria-label="Hour"
                                            onChange={(e) => this.setTimePart('hour12', e.target.value)}
                                        >
                                            <option value="" disabled>HH</option>
                                            {TIME_PICKER_HOURS.map((h) => (
                                                <option key={h} value={h}>{h}</option>
                                            ))}
                                        </select>
                                        <span className="so-time-picker__sep">:</span>
                                        <select
                                            value={minute}
                                            aria-label="Minute"
                                            onChange={(e) => this.setTimePart('minute', e.target.value)}
                                        >
                                            <option value="" disabled>MM</option>
                                            {TIME_PICKER_MINUTES.map((m) => (
                                                <option key={m} value={m}>{m}</option>
                                            ))}
                                        </select>
                                        <div className="so-ampm-toggle">
                                            <button
                                                type="button"
                                                className={period === 'AM' ? 'active' : ''}
                                                onClick={() => this.setTimePart('period', 'AM')}
                                            >
                                                AM
                                            </button>
                                            <button
                                                type="button"
                                                className={period === 'PM' ? 'active' : ''}
                                                onClick={() => this.setTimePart('period', 'PM')}
                                            >
                                                PM
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                <h2 className="so-section-title">Order Summary</h2>
                <div className="so-cart-list">
                    {cartItems.map((item) => (
                        <div className="so-cart-row" key={item.id}>
                            <div className="so-cart-row__info">
                                <p className="so-cart-row__name">{item.name} × {item.qty}</p>
                            </div>
                            <span className="so-cart-row__total">₹{(item.price * item.qty).toFixed(2)}</span>
                        </div>
                    ))}
                </div>
                <div className="so-summary-card">
                    <div className="so-summary-row so-summary-row--total">
                        <span>Total</span>
                        <span>₹{grandTotal.toFixed(2)}</span>
                    </div>
                </div>

                <h2 className="so-section-title">Payment Options</h2>
                <div className="so-payment-list">
                    <div
                        className="so-payment-option"
                        onClick={() => this.setState({ paymentMethod: 'cod' })}
                    >
                        <span className={`so-payment-option__radio${paymentMethod === 'cod' ? ' so-payment-option__radio--checked' : ''}`} />
                        <i className="pi pi-money-bill so-payment-option__icon" />
                        <div className="so-payment-option__body">
                            <span className="so-payment-option__name">Cash on Delivery</span>
                        </div>
                    </div>
                    <div
                        className="so-payment-option so-payment-option--disabled"
                        // onClick={() => this.setState({ paymentMethod: 'online' })}
                    >
                        <span className={`so-payment-option__radio${paymentMethod === 'online' ? ' so-payment-option__radio--checked' : ''}`} />
                        <i className="pi pi-credit-card so-payment-option__icon" />
                        <div className="so-payment-option__body">
                            <span className="so-payment-option__name">Online Payment</span>
                            <span className="so-payment-option__note">UPI / Card / Netbanking</span>
                        </div>
                    </div>
                </div>

                <div className="so-order-bar">
                    <div className="so-order-bar__inner">
                        <div className="so-order-bar__row">
                            <div className="so-order-bar__summary">
                                Total
                                <strong>₹{grandTotal.toFixed(2)}</strong>
                            </div>
                            <button
                                type="button"
                                className="so-order-btn"
                                onClick={this.placeOrder}
                                disabled={!this.canPlaceOrder() || isSaving}
                            >
                                Confirm Order <i className="pi pi-check" />
                            </button>
                        </div>
                        {/* A disabled button never fires onClick, so
                            without this the customer (or anyone debugging
                            "nothing happens when I click Confirm Order")
                            gets no signal about what's still missing. */}
                        {validationMessage && (
                            <p className="so-order-bar__hint">{validationMessage}</p>
                        )}
                    </div>
                </div>
            </>
        );
    };

    render() {
        if (!this.state.authReady || !this.state.stageFields) {
            return <div className="so-loader">Loading…</div>;
        }

        const {
            orderPlaced, orderNumber, isSaving, checkoutStep,
            keypadOpen, keypadItemName, keypadValue,
        } = this.state;

        if (orderPlaced) {
            return (
                <div className="so-confirm">
                    <div className="so-confirm__icon"><i className="pi pi-check" /></div>
                    <h2 className="so-confirm__title">Order Placed!</h2>
                    <p className="so-confirm__body">
                        {orderNumber && (
                            <>Your order number is <span className="so-confirm__number">{orderNumber}</span>.<br /></>
                        )}
                        We'll have it ready for your requested date.
                    </p>
                    {this.renderOnlinePaymentStatus()}
                </div>
            );
        }

        return (
            <div className="so-page">
                {isSaving && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />

                {/* Top-right corner, on every step: the information pages, and
                    beside them the staff way in — "/" lands employees on this page
                    too, so it can't be footer-only. */}
                <EmployeeLoginLink className="so-staff-link" />
                <SpecialOrderMenu />

                {checkoutStep === 'browse' && this.renderBrowseStep()}
                {checkoutStep === 'cart' && this.renderCartStep()}
                {checkoutStep === 'checkout' && this.renderCheckoutStep()}

                {keypadOpen && (
                    <QtyKeypadModal
                        itemName={keypadItemName}
                        value={keypadValue}
                        onDigit={this.pressKeypadDigit}
                        onBackspace={this.pressKeypadBackspace}
                        onClear={this.pressKeypadClear}
                        onConfirm={this.confirmQtyKeypad}
                        onCancel={this.closeQtyKeypad}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    loggedUserDetail: state.auth.userObject,
    menuitems: state.master.masterData?.GetinvMenuItemList || [],
    MenuCategory: state.keyword.MenuCategory || [],
});

const mapDispatchToProps = (dispatch) => ({
    onAuth: (u, p, isSignup, Ar, mobile) => dispatch(actions.auth(u, p, isSignup, Ar, mobile)),
    getMasterKeywordList: (value) => dispatch(actions.getMasterKeywordList(value)),
    getMasterData: (tableUrl) => dispatch(actions.getMasterData(tableUrl)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SpecialOrderDetails);
